import React, {useMemo} from 'react';
import RevealTime from "./SettingOptions/Bookings/RevealTime";
import * as yup from "yup";
import {t} from "../../../../../services/i18n";
import {store} from "../../../../../redux/store";
import ClassSwapping from "./SettingOptions/Bookings/ClassSwapping";
import BookingNotifications from "./SettingOptions/Notifications/BookingNotifications";
import WaitListEntrance from "./SettingOptions/WaitingList/WaitListEntrance";
import CancellationTime from "./SettingOptions/WaitingList/CancellationTime";
import SmsNotification from "./SettingOptions/Notifications/SmsNotification";
import AppointmentPolicy from "./SettingOptions/AppointmentPolicy/AppointmentPolicy";
import EmbedSchedule from "./SettingOptions/EmbedSchedule/EmbedSchedule";
import {isMobile} from "../../../../../services/Helpers";
import WaitListEntranceApproval from './SettingOptions/WaitingList/WaitListEntranceApproval';
import QuickViewDisplay from "./SettingOptions/EmbedSchedule/QuickViewDisplay";
import CustomFields from "./SettingOptions/CustomFields/CustomFields";
import {
    closeModal,
    FLOW_MANAGER_MODAL,
    openModal,
    SCHEDULE_SETTINGS_MODAL
} from "../../../../../redux/modalManagerSlice";
import AutoMessages from "./SettingOptions/Notifications/AutoMessages";
import ActiveIndicator from "./SettingOptions/Notifications/ActiveIndicator";
import {modalFlowOptions} from "../../../Popups/ModalFlow/ModalFlowOptions";
import WhatsappNotification from './SettingOptions/Notifications/WhatsappNotification';
import {MISSING_USER_NOTE_INDICATOR, SCHEDULE_SETTINGS, TRIAL_LIMIT_SETTING} from '../../../../../services/ConstHelper';
import QrNotification from './SettingOptions/Notifications/QrNotification';
import TrialLimit from "./SettingOptions/WaitingList/TrialLimit";
import MissingUserNote from "./SettingOptions/AppointmentPolicy/MissingUserNote";
import AppointmentConfirmation from "./SettingOptions/AppointmentConfirmation/AppointmentConfirmation";

export const getScheduleSettingsConfig = (has, dispatch, hasLock) => {
    const box = store.getState().box.box
    return [
        {
            id: 'bookings',
            title: 'bookings',
            options: [
                {
                    header: 'futureSessionRevealingTime',
                    subheader: t('SetTimeToRevealTxt'),
                    explanation: 'SetTimeToRevealTxt',
                    tooltipText: t('display-classes-tooltip-text'),
                    hide: has('classesLock'),
                    body: <RevealTime/>,
                    validationSchema: yup.object().shape({
                        revealWeeks: yup.number().min(0, t('onlyPositive')).required(t('thisFieldIsMandatory')).typeError(t('onlyPositive')),
                        revealDay: yup.number().min(0).max(6).required(t('thisFieldIsMandatory')).typeError(t('thisFieldIsMandatory')),
                        revealTime: yup.string().nullable().typeError(t('thisFieldIsMandatory'))
                    }),
                    initialValues: {revealWeeks: box.showing_classes_week_ago || 0, revealDay: box.showing_classes_day_ago || 0, revealTime: box.showing_classes_time_ago || null}
                },
                {
                    header: 'sessionSwappingLbl',
                    subheader: `${box.schedule_swapping_time} ${t('hoursAfterCancellation')}`,
                    explanation: 'sessionSwappingTxt',
                    tooltipText: t('classes-swapping-tooltip-text'),
                    body: <ClassSwapping/>,
                    validationSchema: yup.object().shape({
                        swapMinutes: yup.number().min(0, t('onlyPositive')).required(t('thisFieldIsMandatory')).typeError(t('thisFieldIsMandatory')),
                        allowWaitlist: yup.boolean(),
                        swapMaxHours: yup.number().min(0, t('onlyPositive')).max(24, t('max-hours-24')).nullable()
                    }),
                    initialValues: {
                        swapMinutes: box.schedule_swapping_time || 0, allowWaitlist: box.schedule_swapping_standby_time > 0,
                        swapMaxHours: box.schedule_swap_max_hours === undefined ? (box.settings?.[SCHEDULE_SETTINGS]?.schedule_swap_max_hours || null) : box.schedule_swap_max_hours
                    }
                },
            ]
        },
        {
            id: 'notifications',
            title: 'notificationsSettingsLbl',
            options: [
                {
                    header: isMobile ? 'bookingReminderLbl' : '',
                    subheader: t('bookingReminderMobileTxt'),
                    body: isMobile ? null : <AutoMessages
                        messageType={autoMessageTypes.REMINDER_SESSION} sessionType={null}
                        header={t('bookingReminderLbl')} subheader={t('bookingReminderMobileTxt')}
                        periodAmountTextBefore={t('class-session-period-before')}
                        periodAmountTextAfter={t('class-session-period-after')}
                        sectionHeader={{title: t('autoMessageNotificationsLbl'), tooltip: t('session-auto-message-tooltip')}}/>,
                    validationSchema: yup.object().shape({}),
                    initialValues: {},
                    hideDivider: true,
                    headerStart: <ActiveIndicator isActive={getIsMessageTypeActive(autoMessageTypes.REMINDER_SESSION, null)}/>,
                    onClick: () => openAutoMessageModal(dispatch, autoMessageTypes.REMINDER_SESSION, null)
                },
                {
                    header: isMobile ? 'markCheckInMobileLbl' : '',
                    subheader: t('markCheckInMobileTxt'),
                    body: isMobile ? null : <AutoMessages messageType={autoMessageTypes.MARK_CHECK_IN} sessionType={null} header={t('markCheckInLbl')} subheader={t('markCheckInTxt')}/>,
                    validationSchema: yup.object().shape({}),
                    initialValues: {},
                    headerStart: <ActiveIndicator isActive={getIsMessageTypeActive(autoMessageTypes.MARK_CHECK_IN, null)}/>,
                    onClick: () => openAutoMessageModal(dispatch, autoMessageTypes.MARK_CHECK_IN, null)
                },
                {
                    header: isMobile ? 'smsNotificationLbl' : '',
                    subheader: t('smsNotificationTxt'),
                    explanation: null,
                    hide: has('classesLock'),
                    body: isMobile ? null : <SmsNotification/>,
                    sideOverride: isMobile ? <SmsNotification/> : null,
                    validationSchema: yup.object().shape({
                        allowSmsWaitList: yup.boolean()
                    }),
                    initialValues: { allowSmsWaitList: box.allow_sms_to_waiting_list || 0 }
                },
                ...(box.integrations?.find(i => i.name === 'greenApi') ? [{
                    header: isMobile ? 'whatsappNotificationLbl' : '',
                    subheader: t('whatsappNotificationTxt'),
                    explanation: null,
                    body: isMobile ? null : <WhatsappNotification/>,
                    sideOverride: isMobile ? <WhatsappNotification/> : null,
                    validationSchema: yup.object().shape({
                        allowWhatsappWaitList: yup.boolean().nullable()
                    }),
                    initialValues: { allowWhatsappWaitList: box.allow_whatsapp_to_waiting_list ?? (box.settings?.[SCHEDULE_SETTINGS]?.allow_whatsapp_to_waiting_list || 0) }
                }] : []),
                {
                    header: isMobile ? 'qrNotificationLbl' : '',
                    subheader: t('qrNotificationTxt'),
                    explanation: null,
                    permission: 'viewFreefit',
                    body: isMobile ? null : <QrNotification/>,
                    sideOverride: isMobile ? <QrNotification/> : null,
                    validationSchema: yup.object().shape({
                        sendQrMessage: yup.boolean()
                    }),
                    initialValues: { sendQrMessage: box.send_qr_messsage || false }
                },
            ]
        },
        {
            id: 'waitingList',
            title: 'waitingListSettingsLbl',
            hide: has('classesLock'),
            options: [
                {
                    header: 'waitListEntranceApproval',
                    subheader: null,
                    tooltipText: t('waitListEntranceApprovalTxt'),
                    body: <WaitListEntranceApproval/>,
                    validationSchema: yup.object().shape({
                        standbyTimeToApprove: yup.number().nullable().min(1, t('time-to-approve-range-1-60')).max(60, t('time-to-approve-range-1-60')).typeError(t('only-numbers'))
                    }),
                    initialValues: { standbyTimeToApprove: box.standby_time_to_approve},
                    permission: 'waitingListApproval'
                },
                {
                    header: 'ignoreWaitingListLbl',
                    subheader: `${box.schedule_standby_offset} ${t('beforeClassStarts')}`,
                    explanation: 'stopAddingClientsTxt',
                    // tooltipText: t('ignoreWaitingList-tooltip-text'),
                    body: <WaitListEntrance/>,
                    validationSchema: yup.object().shape({
                        waitListOffset: yup.number().min(0, t('onlyPositive')).required(t('thisFieldIsMandatory')).typeError(t('thisFieldIsMandatory'))
                    }),
                    initialValues: { waitListOffset: box.schedule_standby_offset || 0}
                },
                {
                    header: isMobile ? 'cancelationTimeLbl' : '',
                    subheader: `${box.standby_cancellation_time} ${t('afterJoining')}`,
                    explanation: 'cancelationTimeTxt',
                    // tooltipText: t('cancellation-time-tooltip-text'),
                    body: <CancellationTime/>,
                    validationSchema: yup.object().shape({
                        cancellationTime: yup.number().min(0, t('onlyPositive')).required(t('thisFieldIsMandatory')).typeError(t('thisFieldIsMandatory'))
                    }),
                    initialValues: { cancellationTime: box.standby_cancellation_time || 0}
                },
            ]
        },
        {
            id: 'trial',
            title: 'trialLbl',
            hide: !((has('bookForTrialClassSite') || has('bookForTrialHugim')) && !has('bookForTrialClassSiteLock')),
            options: [
                {
                    header: 'trialLimitHeader',
                    subheader: getTrialLimit() === 'unlimited' ? t('trial-unlimited-mobile') : t('trial-limit-to', {limit: getTrialLimit()}),
                    explanation: 'trialLimitHeaderTxt',
                    body: <TrialLimit/>,
                    validationSchema: yup.object().shape({
                        trialLimit: yup.mixed().required(t('thisFieldIsMandatory')).typeError(t('thisFieldIsMandatory'))
                            .test('is-number-or-unlimited', t('onlyPositive'), (value) => {
                                return value === 'unlimited' || (typeof value === 'number' && value >= 1);
                            })
                    }),
                    initialValues: { trialLimit: getTrialLimit()},
                },
                {
                    header: isMobile ? 'missedTrialSessionMobileLbl' : '',
                    subheader: t('missedTrialSessionMobileLbl'),
                    body: isMobile ? null :
                        <AutoMessages
                        messageType={autoMessageTypes.MISSED_TRIAL_SESSION}
                        header={t('missedTrialSessionLbl')}
                        subheader={t('missedTrialSessionTxt')}
                        sectionHeader={{title: t('autoMessageTrialLbl'), tooltip: t('trial-auto-message-tooltip')}}
                        periodAmountTextBefore={t('trial-session-period-before')}
                        periodAmountTextAfter={t('trial-session-period-after')}
                        sessionType={null}
                        />,
                    validationSchema: yup.object().shape({}),
                    initialValues: {},
                    headerStart: <ActiveIndicator isActive={getIsMessageTypeActive(autoMessageTypes.MISSED_TRIAL_SESSION, null)}/>,
                    hideDivider: true,
                    permission: 'appointmentAutoMessages',
                    onClick: () => openAutoMessageModal(dispatch, autoMessageTypes.MISSED_TRIAL_SESSION, null)
                },
            ]
        },
        {
            id: 'cancellation',
            title: 'cancellationsPolicy',
            hasLock: has('appointmentsLock'),
            options: [
                {
                    header: isMobile ? 'sessionReminderMobileLbl' : '',
                    subheader: t('sessionReminderMobileTxt'),
                    body: isMobile ? null : <AutoMessages messageType={autoMessageTypes.REMINDER_SESSION} sessionType={'appointment'} header={t('sessionReminderLbl')} subheader={t('sessionReminderTxt')} sectionHeader={{title: t('autoMessageLbl'), tooltip: t('session-auto-message-tooltip-appointments')}}/>,
                    validationSchema: yup.object().shape({}),
                    initialValues: {},
                    headerStart: <ActiveIndicator isActive={getIsMessageTypeActive(autoMessageTypes.REMINDER_SESSION)}/>,
                    hideDivider: true,
                    permission: 'appointmentAutoMessages',
                    onClick: () => openAutoMessageModal(dispatch, autoMessageTypes.REMINDER_SESSION)
                },
                {
                    header: isMobile ? 'secondSessionReminderMobileLbl' : '',
                    subheader: t('secondSessionReminderMobileTxt'),
                    hide: !getIsMessageTypeActive(autoMessageTypes.REMINDER_SESSION),
                    body: isMobile ? null : <AutoMessages messageType={autoMessageTypes.SECOND_REMINDER_SESSION} sessionType={'appointment'} header={t('secondSessionReminderLbl')} subheader={t('secondSessionReminderTxt')}/>,
                    validationSchema: yup.object().shape({}),
                    initialValues: {},
                    headerStart: <ActiveIndicator isActive={getIsMessageTypeActive(autoMessageTypes.SECOND_REMINDER_SESSION)}/>,
                    hideDivider: true,
                    permission: 'appointmentAutoMessages',
                    onClick: () => openAutoMessageModal(dispatch, autoMessageTypes.SECOND_REMINDER_SESSION)
                },
                {
                    header: isMobile ? 'bookSessionAutoMessageMobileLbl' : '',
                    subheader: t('bookSessionReminderMobileTxt'),
                    body: isMobile ? null : <AutoMessages messageType={autoMessageTypes.BOOK_SESSION} sessionType={'appointment'} header={t('bookSessionAutoMessageLbl')} subheader={t('bookSessionReminderTxt')}/>,
                    validationSchema: yup.object().shape({}),
                    initialValues: {},
                    headerStart: <ActiveIndicator isActive={getIsMessageTypeActive(autoMessageTypes.BOOK_SESSION)}/>,
                    hideDivider: true,
                    permission: 'appointmentAutoMessages',
                    onClick: () => openAutoMessageModal(dispatch, autoMessageTypes.BOOK_SESSION)
                },
                {
                    header: isMobile ? 'editSessionAutoMessageMobileLbl' : '',
                    subheader: t('editSessionReminderMobileTxt'),
                    body: isMobile ? null : <AutoMessages messageType={autoMessageTypes.EDIT_SESSION} sessionType={'appointment'} header={t('editSessionAutoMessageLbl')} subheader={t('editSessionReminderTxt')}/>,
                    validationSchema: yup.object().shape({}),
                    initialValues: {},
                    headerStart: <ActiveIndicator isActive={getIsMessageTypeActive(autoMessageTypes.EDIT_SESSION)}/>,
                    hideDivider: true,
                    permission: 'appointmentAutoMessages',
                    onClick: () => openAutoMessageModal(dispatch, autoMessageTypes.EDIT_SESSION)
                },
                {
                    header: isMobile ? 'cancelSessionAutoMessageMobileLbl' : '',
                    subheader: t('cancelSessionReminderMobileTxt'),
                    body: isMobile ? null : <AutoMessages messageType={autoMessageTypes.CANCEL_SESSION} sessionType={'appointment'} header={t('cancelSessionAutoMessageLbl')} subheader={t('cancelSessionReminderTxt')}/>,
                    validationSchema: yup.object().shape({}),
                    initialValues: {},
                    headerStart: <ActiveIndicator isActive={getIsMessageTypeActive(autoMessageTypes.CANCEL_SESSION)}/>,
                    permission: 'appointmentAutoMessages',
                    onClick: () => openAutoMessageModal(dispatch, autoMessageTypes.CANCEL_SESSION)
                },
                { //FOR MERGE CONFILICTS - THIS SHOULD BE AFTER AUTO ACTIONS SETTINGS AND BEFORE CONFIRMATION SETTINGS!!
                    header: isMobile ? 'missingUserNoteLbl' : '',
                    subheader: t('missingUserNoteTxt'),
                    explanation: null,
                    permission: 'userNoteView',
                    body: isMobile ? null : <MissingUserNote/>,
                    sideOverride: isMobile ? <MissingUserNote/> : null,
                    validationSchema: yup.object().shape({
                        showMissingUserNote: yup.boolean()
                    }),
                    initialValues: { showMissingUserNote: getMissingUserNoteIndicator() }
                },
                {
                    header: isMobile ? 'appointmentConfirmation' : '',
                    subheader: null,
                    explanation: null,
                    body: <AppointmentConfirmation/>,
                    validationSchema: yup.object().shape({
                        confirmationSettings: yup.object().shape({
                            showStatusOnCalendar: yup.boolean().required(t('thisFieldIsMandatory')).typeError(t('thisFieldIsMandatory')),
                            confirmationText: yup.string().nullable()
                        })
                    }),
                    initialValues: {
                        confirmationSettings: {
                            showStatusOnCalendar: box.settings?.confirmation_settings?.showStatusOnCalendar || false,
                            confirmationText: box.settings?.confirmation_settings?.confirmationText
                        }
                    },
                    permission: 'arrivalConfirmations',
                    hide: has('arrivalConfirmations') && hasLock('arrivalConfirmations')
                },
                {
                    header: isMobile ? 'cancellationsPolicy' : '',
                    subheader: null,
                    explanation: null,
                    body: <AppointmentPolicy/>,
                    validationSchema: yup.object().shape({
                        apptCancelTime: yup.number().min(0, t('onlyPositive')).typeError(t('thisFieldIsMandatory')),
                        apptCancelEnabled: yup.boolean().required(t('thisFieldIsMandatory')).typeError(t('thisFieldIsMandatory')),
                        apptCancelPolicy: yup.string().nullable()
                    }),
                    initialValues: { apptCancelTime: box?.locations_box[0].availability_time_cancellation || 0,
                                     apptCancelEnabled: box?.locations_box[0].availability_enable_late_cancellation || false,
                                     apptCancelPolicy: box?.locations_box[0].cancellation_policy }
                },
            ]
        },
        {
            id: 'embedSchedule',
            title: 'embedScheduleLabel',
            hideOnMobile: true,
            options: [
                {
                    header: '',
                    subheader: null,
                    explanation: null,
                    body: <QuickViewDisplay />,
                    validationSchema: yup.object().shape({
                        showSchedulePreview: yup.number().min(0)
                    }),
                    initialValues: { showSchedulePreview: box?.show_schedule_preview },
                    hide: box.isBoxHasNewSite
                },
                {
                    header: 'embedSchedule',
                    subheader: null,
                    explanation: null,
                    body: <EmbedSchedule/>,
                    validationSchema: yup.object().shape({}),
                    initialValues: {}
                },
            ]
        },
        ...((has('customFieldSettingsLock') || has('editCustomField') || has('deleteCustomField') || has('addCustomField')) ? [{
            id: 'customFields',
            title: 'customFieldsLabel',
            hasLock: has('customFieldSettingsLock'),
            optionalLockCallback: () => dispatch(closeModal({ modalName: SCHEDULE_SETTINGS_MODAL })),
            options: [
                {
                    header: 'customFieldsLabel',
                    subheader: null,
                    explanation: null,
                    body: <CustomFields />,
                    initialValues: { showSchedulePreview: box?.show_schedule_preview }
                }
            ]
        }] : []),
        {
            id: 'quickView',
            title: 'quickViewLabel',
            hideOnMobile: true,
            hide: !box.isBoxHasNewSite,
            options: [
                {
                    header: '',
                    subheader: null,
                    explanation: null,
                    body: <QuickViewDisplay />,
                    validationSchema: yup.object().shape({
                        showSchedulePreview: yup.number().min(0)
                    }),
                    initialValues: { showSchedulePreview: box?.show_schedule_preview }
                },
            ]
        },
    ]
}

const getTeamNotificationSubheader = (box) => {
    switch (box.notify_session_owner) {
        case 1:
            return 'notifySessionOwnerBySms'
        case 2:
            return 'notifySessionOwnerByEmail'
        case 3:
            return 'notifySessionOwnerBySmsAndEmail'
        default:
            return null
    }
}

export const autoMessageTypes = {
    MARK_CHECK_IN: 'MARK_CHECK_IN',
    BOOK_SESSION: 'BOOK_SESSION',
    EDIT_SESSION: 'EDIT_SESSION',
    CANCEL_SESSION: 'CANCEL_SESSION',
    REMINDER_SESSION: 'REMINDER_SESSION',
    SECOND_REMINDER_SESSION: 'SECOND_REMINDER_SESSION',
    MISSED_TRIAL_SESSION: 'MISSED_TRIAL_SESSION'
}

export const autoMessageTimeUnits = {
    HOUR: 'HOUR',
    DAY: 'DAY',
    WEEK: 'WEEK',
}

const openAutoMessageModal = (dispatch, messageType, sessionType = 'appointment') => {
    const box = store.getState().box.box
    const autoActions = box?.auto_actions
    const messageTypeAutoActions = autoActions && autoActions.length ? autoActions.filter(item => item.auto_actions.action === messageType && item.session_type === sessionType) : []
    dispatch(openModal({
        modalName: FLOW_MANAGER_MODAL,
        props: {
            initSelection: modalFlowOptions.SESSION_AUTO_MESSAGE,
            initProps: {messageType, sessionType},
            target: messageTypeAutoActions,
            onSubmit: (values) => console.log('save this', values),
        },
        onClose: () => console.log('closed')
    }))
}

export const getIsMessageTypeActive = (messageType, sessionType = 'appointment') => {
    const box = store.getState().box.box
    const autoActions = box?.auto_actions
    const messageTypeAutoActions = autoActions && autoActions.length ? autoActions.filter(item => item.auto_actions.action === messageType && item.session_type === sessionType) : []
    return messageTypeAutoActions.some(item => item.active);
}

const getTrialLimit = () => {
    const box = store.getState().box.box
    const trialLimit = box?.settings?.[TRIAL_LIMIT_SETTING]
    return trialLimit?.limit ?? 1
}

const getMissingUserNoteIndicator = () => {
    const box = store.getState().box.box
    const missingUserNote = box?.settings?.[MISSING_USER_NOTE_INDICATOR]
    return missingUserNote?.active ?? false
}